import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import Button from "components/ui/controls/button/button";
import TextField from "components/ui/controls/text-field/text-field";
import { FaEdit, FaFileInvoiceDollar, FaUpload } from "react-icons/fa";
import { applyFormat } from "components/ui/ui-helpers";
import { createStyledButton } from "components/ui/controls/button/button-styles";

/**
 * Composant de carte financière ultra-moderne et compacte
 * Optimisé pour l'affichage de métriques financières avec une densité d'information maximale
 */
const CompactFinancialCard = ({
  icon,
  title,
  value,
  onUpload,
  uploadDisabled = false,
  onEdit,
  idOnEdit,
  bgColor,
  iconColor = "#f3f4f6",
  textColor = "#000000",
  isEditable = false,
  onChange,
  field,
  format,
  dataType = "uint",
  // Nouvelles props pour les couleurs
  accentColor, // Couleur du trait et de l'icône
  theme, // Thème prédéfini
  rightIcon, // Icône à droite du titre
}) => {
  const [tooltipRect, setTooltipRect] = useState(null);
  const uploadBtnRef = useRef(null);
  // Déterminer la couleur d'accent à utiliser
  const finalAccentColor = accentColor || theme?.accentColor || icon.props.style.color;

  return (
    <div
      className="compact-financial-card-modern param-resultat-card"
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        border: "1px solid rgba(255,255,255,0.8)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "34px",
        position: "relative",
        overflow: "visible",
        background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
        backdropFilter: "blur(12px)",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1), 0 3px 10px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.9)";
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(252,252,255,0.98) 100%)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)";
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)";
      }}
    >
      {/* Ligne d'accent colorée moderne avec effet de brillance */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          background: `linear-gradient(135deg, ${finalAccentColor}, ${finalAccentColor}aa, ${finalAccentColor}66)`,
          borderRadius: "0 2px 2px 0",
          opacity: 0.9,
          boxShadow: `2px 0 8px ${finalAccentColor}33`,
        }}
      />
      {/* Élément décoratif en arrière-plan */}
      <div
        style={{
          position: "absolute",
          right: "-10px",
          top: "-10px",
          width: "40px",
          height: "40px",
          background: `radial-gradient(circle, ${finalAccentColor}15, transparent 70%)`,
          borderRadius: "50%",
          opacity: 0.6,
        }}
      />
      {/* Contenu principal */}
      <div style={{ display: "flex", alignItems: "center", flex: 1, marginLeft: "8px", zIndex: 1 }}>
        {/* Container d'icône moderne */}
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "8px",
            background: `linear-gradient(135deg, ${iconColor}, ${iconColor}dd)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "10px",
            boxShadow: `0 2px 6px ${finalAccentColor}25, 0 1px 3px rgba(0,0,0,0.08)`,
            border: `1px solid ${finalAccentColor}20`,
            transition: "all 0.2s ease",
            position: "relative",
          }}
        >
          {/* Effet de brillance sur l'icône */}
          <div
            style={{
              position: "absolute",
              top: "2px",
              left: "2px",
              right: "2px",
              height: "50%",
              background: "linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)",
              borderRadius: "6px 6px 8px 8px",
              pointerEvents: "none",
            }}
          />
          {React.cloneElement(icon, {
            style: {
              fontSize: "12px",
              color: finalAccentColor,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
              zIndex: 2,
              position: "relative",
            },
          })}
        </div>
        {/* Titre moderne */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          
          <h4
            style={{
              margin: 0,
              fontSize: "14px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#000000",
              marginBottom: "0px",
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
            }}
          >
            {title}
          </h4>
          {/* Icône à droite du titre */}
          {rightIcon && (
            <div
              style={{
                marginLeft: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {rightIcon}
            </div>
          )}
        </div>
      </div>
      {/* Actions et valeur */}
      <div  style={{ display: "flex", alignItems: "center", gap: "8px", zIndex: 1 }}>
        {onUpload && (
          <div ref={uploadBtnRef} style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Button
              onClick={onUpload}
              disabled={uploadDisabled}
              icon
              {...createStyledButton("card-action", "download", {}, uploadDisabled)}
              onMouseEnter={() => {
                if (uploadBtnRef.current) setTooltipRect(uploadBtnRef.current.getBoundingClientRect());
              }}
              onMouseLeave={() => setTooltipRect(null)}
            >
              <FaUpload style={{ color: "#8b5cf6", fontSize: "12px" }} />
            </Button>
            {tooltipRect && ReactDOM.createPortal(
              <div style={{
                position: "fixed",
                top: tooltipRect.bottom + 6,
                right: window.innerWidth - tooltipRect.right,
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                padding: "14px 16px",
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                borderLeft: "3px solid #3b82f6",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(59,130,246,0.22)",
                whiteSpace: "nowrap",
                zIndex: 999999,
                pointerEvents: "none",
              }}>
                <FaFileInvoiceDollar style={{ color: "#3b82f6", fontSize: "13px", flexShrink: 0, marginTop: "2px" }} />
                <span style={{ fontSize: "11px", color: "#1e40af", lineHeight: "1.6", fontFamily: "Inter, sans-serif" }}>
                  Importez votre avis d'imposition pour renseigner automatiquement<br />
                  les <strong>revenus</strong>, <strong>déductions fiscales</strong>, <strong>crédits</strong> et <strong>réductions d'impôt</strong>.
                </span>
              </div>,
              document.body
            )}
          </div>
        )}
        {/* Bouton d'édition moderne */}
        {onEdit && (
          <Button onClick={onEdit} icon {...createStyledButton("card-action", "edit")}>
            <FaEdit id={idOnEdit || undefined} style={{ color: "#22c55e", fontSize: "12px" }} />
          </Button>
        )}
        {/* Valeur ou champ éditable moderne */}
        <div
          style={{
            fontSize: "14px",
            fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: 400, // Force le non-gras sur le parent
            color: "#000000",
            letterSpacing: "-0.02em",
            width: "100px", // Largeur fixe pour alignement parfait, identique au badge
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            height: "26px",
            paddingRight: "8px",
          }}
        >
          {isEditable ? (
            <TextField
              value={value}
              onChange={onChange}
              dataType={dataType}
              format={format || { thousands: " ", unit: " €" }}
              style={{
                height: "26px",
                width: "100px",
                padding: "0 8px",
                backgroundColor: `${finalAccentColor}15`,
                border: `1px solid ${finalAccentColor}30`,
                borderRadius: "6px",
                textAlign: "right",
                fontSize: "12px",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                color: "#000000",
                letterSpacing: "-0.02em",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: `inset 0 1px 2px ${finalAccentColor}10`,
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = `${finalAccentColor}60`;
                e.currentTarget.style.backgroundColor = `${finalAccentColor}08`;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${finalAccentColor}20, inset 0 1px 2px ${finalAccentColor}15`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = `${finalAccentColor}30`;
                e.currentTarget.style.backgroundColor = `${finalAccentColor}15`;
                e.currentTarget.style.boxShadow = `inset 0 1px 2px ${finalAccentColor}10`;
              }}
            />
          ) : (
            <div
              style={{
                textAlign: "right",
                width: "100px",
                height: "26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                fontSize: "12px",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 400,
                color: "#000000",
                letterSpacing: "-0.02em",
                paddingRight: "8px",
              }}
            >
              {format ? applyFormat(value || 0, dataType, format) : value || 0}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Composant de carte financière ultra-moderne et compacte avec deux colonnes
 * Optimisé pour l'affichage de métriques financières pour client et conjoint
 */
/**
 * Composant de carte financière ultra-moderne et compacte avec trois colonnes
 * Optimisé pour l'affichage de métriques financières avec trois valeurs (ex: 100% Prime, 100% Dividendes, Optimum)
 */
export const CompactFinancialCardThreeColumn = ({
  icon,
  title,
  valueColumn1,
  valueColumn2,
  valueColumn3,
  onEdit,
  bgColor,
  iconColor = "#f3f4f6",
  textColor = "#000000",
  isEditable = false,
  onChange,
  field,
  format,
  dataType = "uint",
  // Nouvelles props pour les couleurs
  accentColor, // Couleur du trait et de l'icône
  theme, // Thème prédéfini
  isTitle = false, // Pour les lignes de titre
}) => {
  // Déterminer la couleur d'accent à utiliser
  const finalAccentColor = accentColor || theme?.accentColor || icon?.props?.style?.color || "#6b7280";

  return (
    <div
      className="compact-financial-card-modern param-resultat-card"
      style={{
        backgroundColor: isTitle ? "#f8fafc" : "#ffffff",
        borderRadius: "12px",
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        border: "1px solid rgba(255,255,255,0.8)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "38px",
        position: "relative",
        overflow: "hidden",
        background: isTitle 
          ? "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.98) 100%)"
          : "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
        backdropFilter: "blur(12px)",
        margin: "2px 0",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1), 0 3px 10px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.9)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)";
      }}
    >
      {/* Ligne d'accent colorée moderne avec effet de brillance */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          background: `linear-gradient(135deg, ${finalAccentColor}, ${finalAccentColor}aa, ${finalAccentColor}66)`,
          borderRadius: "0 2px 2px 0",
          opacity: 0.9,
          boxShadow: `2px 0 8px ${finalAccentColor}33`,
        }}
      />
      
      {/* Élément décoratif en arrière-plan */}
      <div
        style={{
          position: "absolute",
          right: "-10px",
          top: "-10px",
          width: "40px",
          height: "40px",
          background: `radial-gradient(circle, ${finalAccentColor}15, transparent 70%)`,
          borderRadius: "50%",
          opacity: 0.6,
        }}
      />
      
      {/* Contenu principal */}
      <div style={{ display: "flex", alignItems: "center", flex: 1.5, marginLeft: "8px", zIndex: 1 }}>
        {/* Container d'icône moderne */}
        {icon && (
          <div
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "8px",
              background: `linear-gradient(135deg, ${iconColor}, ${iconColor}dd)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "10px",
              boxShadow: `0 2px 6px ${finalAccentColor}25, 0 1px 3px rgba(0,0,0,0.08)`,
              border: `1px solid ${finalAccentColor}20`,
              transition: "all 0.2s ease",
              position: "relative",
            }}
          >
            {/* Effet de brillance sur l'icône */}
            <div
              style={{
                position: "absolute",
                top: "2px",
                left: "2px",
                right: "2px",
                height: "50%",
                background: "linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)",
                borderRadius: "6px 6px 8px 8px",
                pointerEvents: "none",
              }}
            />
            {React.cloneElement(icon, {
              style: {
                fontSize: "12px",
                color: finalAccentColor,
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                zIndex: 2,
                position: "relative",
              },
            })}
          </div>
        )}
        
        {/* Titre moderne */}
        <div style={{ flex: 1 }}>
          <h4
            style={{
              margin: 0,
              fontSize: "13px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: isTitle ? 600 : 400,
              color: isTitle ? "#1f2937" : "#374151",
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </h4>
        </div>
      </div>

      {/* Actions et valeurs en trois colonnes avec espacement */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", zIndex: 1, flex: 3 }}>
        {/* Bouton d'édition moderne */}
        {onEdit && (
          <Button onClick={onEdit} icon {...createStyledButton("card-action", "cardEdit")}>
            <FaEdit style={{ color: "#22c55e", fontSize: "12px" }} />
          </Button>
        )}

        {/* Colonne 1 - 100% Prime */}
        <div 
          style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            flex: 0.8,
            padding: "6px 8px",
            borderRadius: "6px",
            border: "1px solid #c6d4cdff",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              textAlign: "center",
            }}
          >
            {format ? applyFormat(valueColumn1 || 0, dataType, format) : valueColumn1 || 0}
          </div>
        </div>

        {/* Colonne 2 - 100% Dividendes */}
        <div 
          style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            flex: 0.8,
            padding: "6px 8px",
            borderRadius: "6px",
            border: "1px solid #c6d4cdff",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              textAlign: "center",
            }}
          >
            {format ? applyFormat(valueColumn2 || 0, dataType, format) : valueColumn2 || 0}
          </div>
        </div>

        {/* Colonne 3 - Optimum */}
        <div 
          style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            flex: 0.8,
            padding: "6px 8px",
            borderRadius: "6px",
             border: "1px solid #c6d4cdff",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              textAlign: "center",
            }}
          >
            {format ? applyFormat(valueColumn3 || 0, dataType, format) : valueColumn3 || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

export const CompactFinancialCardTwoColumn = ({
  icon,
  title,
  valueClient,
  valueConjoint,
  labelClient,
  labelConjoint,
  onEdit,
  bgColor,
  iconColor = "#f3f4f6",
  textColor = "#000000",
  isEditable = false,
  onChange,
  field,
  format,
  dataType = "uint",
  // Nouvelles props pour les couleurs
  accentColor, // Couleur du trait et de l'icône
  theme, // Thème prédéfini
}) => {
  // Déterminer la couleur d'accent à utiliser
  const finalAccentColor = accentColor || theme?.accentColor || icon.props.style?.color;

  return (
    <div
      className="compact-financial-card-modern param-resultat-card"
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        border: "1px solid rgba(255,255,255,0.8)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "34px",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
        backdropFilter: "blur(12px)",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1), 0 3px 10px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.9)";
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(252,252,255,0.98) 100%)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)";
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)";
      }}
    >
      {/* Ligne d'accent colorée moderne avec effet de brillance */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          background: `linear-gradient(135deg, ${finalAccentColor}, ${finalAccentColor}aa, ${finalAccentColor}66)`,
          borderRadius: "0 2px 2px 0",
          opacity: 0.9,
          boxShadow: `2px 0 8px ${finalAccentColor}33`,
        }}
      />
      {/* Élément décoratif en arrière-plan */}
      <div
        style={{
          position: "absolute",
          right: "-10px",
          top: "-10px",
          width: "40px",
          height: "40px",
          background: `radial-gradient(circle, ${finalAccentColor}15, transparent 70%)`,
          borderRadius: "50%",
          opacity: 0.6,
        }}
      />
      {/* Contenu principal */}
      <div style={{ display: "flex", alignItems: "center", flex: 1, marginLeft: "8px", zIndex: 1 }}>
        {/* Container d'icône moderne */}
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "8px",
            background: `linear-gradient(135deg, ${iconColor}, ${iconColor}dd)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "10px",
            boxShadow: `0 2px 6px ${finalAccentColor}25, 0 1px 3px rgba(0,0,0,0.08)`,
            border: `1px solid ${finalAccentColor}20`,
            transition: "all 0.2s ease",
            position: "relative",
          }}
        >
          {/* Effet de brillance sur l'icône */}
          <div
            style={{
              position: "absolute",
              top: "2px",
              left: "2px",
              right: "2px",
              height: "50%",
              background: "linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)",
              borderRadius: "6px 6px 8px 8px",
              pointerEvents: "none",
            }}
          />
          {React.cloneElement(icon, {
            style: {
              fontSize: "12px",
              color: finalAccentColor,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
              zIndex: 2,
              position: "relative",
            },
          })}
        </div>
        {/* Titre moderne */}
        <div style={{ flex: 1 }}>
          <h4
            style={{
              margin: 0,
              fontSize: "14px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#000000",
              marginBottom: "0px",
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </h4>
        </div>
      </div>

      {/* Actions et valeurs en deux colonnes */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", zIndex: 1 }}>
        {/* Bouton d'édition moderne */}
        {onEdit && (
          <Button onClick={onEdit} icon {...createStyledButton("card-action", "cardEdit")}>
            <FaEdit style={{ color: "#22c55e", fontSize: "12px" }} />
          </Button>
        )}

        {/* Colonne Client */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: "80px" }}>
          <div
            style={{
              fontSize: "10px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#6b7280",
              marginBottom: "2px",
              letterSpacing: "-0.01em",
            }}
          >
            {labelClient}
          </div>
          <div
            style={{
              fontSize: "12px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#000000",
              letterSpacing: "-0.02em",
              textAlign: "right",
            }}
          >
            {format ? applyFormat(valueClient || 0, dataType, format) : valueClient || 0}
          </div>
        </div>

        {/* Colonne Conjoint */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: "80px" }}>
          <div
            style={{
              fontSize: "10px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#6b7280",
              marginBottom: "2px",
              letterSpacing: "-0.01em",
            }}
          >
            {labelConjoint}
          </div>
          <div
            style={{
              fontSize: "12px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#000000",
              letterSpacing: "-0.02em",
              textAlign: "right",
            }}
          >
            {format ? applyFormat(valueConjoint || 0, dataType, format) : valueConjoint || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Container moderne pour les cartes financières
 */
export const CompactFinancialContainer = ({ title, subtitle, children, className = "", style = {} }) => (
  <div
    className={`compact-financial-container ${className}`}
    style={{
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      borderRadius: "20px",
      padding: "20px",
      margin: "5px 0", // Espacement vertical entre les containers
      boxShadow: "0 8px 32px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)",
      border: "1px solid rgba(255,255,255,0.9)",
      background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
      backdropFilter: "blur(16px)",
      position: "relative",
      overflow: "visible",
      ...style,
    }}
  >
    {/* Élément décoratif en arrière-plan */}
    <div
      style={{
        position: "absolute",
        top: "-50px",
        right: "-50px",
        width: "120px",
        height: "120px",
        background: "radial-gradient(circle, rgba(99, 102, 241, 0.05), transparent 70%)",
        borderRadius: "50%",
        opacity: 0.8,
      }}
    />

    {/* En-tête */}
    {(title || subtitle) && (
      <div
        style={{
          marginBottom: "18px",
          paddingBottom: "14px",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {title && (
          <h3
            style={{
              margin: 0,
              fontSize: "14px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 700,
              color: "#1f2937",
              marginBottom: "4px",
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </h3>
        )}
        {subtitle && (
          <p
            style={{
              margin: 0,
              fontSize: "12px",
              color: "#6b7280",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              letterSpacing: "-0.01em",
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
    )}
    {/* Grille de cartes moderne */}
    <div
      style={{
        display: "grid",
        gap: "5px",
        position: "relative",
        zIndex: 1,
      }}
    >
      {children}
    </div>
  </div>
);

/**
 * Thèmes de couleurs prédéfinis avec couleurs coordonnées pour trait et icônes
 */
export const FINANCIAL_THEMES = {
  success: {
    bgColor: "#ffffff",
    iconColor: "#d1fae5",
    textColor: "#000000",
    accentColor: "#059669", // Vert emeraude
  },
  danger: {
    bgColor: "#ffffff",
    iconColor: "#fecaca",
    textColor: "#000000",
    accentColor: "#dc2626", // Rouge
  },
  primary: {
    bgColor: "#ffffff",
    iconColor: "#dbeafe",
    textColor: "#000000",
    accentColor: "#2563eb", // Bleu
  },
  purple: {
    bgColor: "#ffffff",
    iconColor: "#e9d5ff",
    textColor: "#000000",
    accentColor: "#7c3aed", // Violet
  },
  orange: {
    bgColor: "#ffffff",
    iconColor: "#fed7aa",
    textColor: "#000000",
    accentColor: "#ea580c", // Orange
  },
  neutral: {
    bgColor: "#ffffff",
    iconColor: "#f3f4f6",
    textColor: "#000000",
    accentColor: "#6b7280", // Gris
  },
  // Nouveaux thèmes
  teal: {
    bgColor: "#ffffff",
    iconColor: "#ccfbf1",
    textColor: "#000000",
    accentColor: "#14b8a6", // Turquoise
  },
  indigo: {
    bgColor: "#ffffff",
    iconColor: "#e0e7ff",
    textColor: "#000000",    accentColor: "#6366f1", // Indigo
  },
  rose: {
    bgColor: "#ffffff",
    iconColor: "#fce7f3",
    textColor: "#000000",
    accentColor: "#ec4899", // Rose
  },
  amber: {
    bgColor: "#ffffff",
    iconColor: "#fef3c7",
    textColor: "#000000",
    accentColor: "#f59e0b", // Ambre
  },  prevoyance: {
    bgColor: "#ffffff",
    iconColor: "#d1fae5", // Fond d'icône vert pâle plus vif
    textColor: "#000000",
    accentColor: "#22c55e", // Vert plus vif
  },
};

/**
 * Composant de carte financière ultra-moderne et compacte pour l'optimisation de rémunération
 * Optimisé pour l'affichage avec 3 colonnes : Origine, Nouvelle (éditable), Écart
 */
export const CompactFinancialOptiRem = ({
  icon,
  title,
  valueOrigine,
  valueNouvelle,
  valueEcart,
  onEdit,
  bgColor,
  iconColor = "#f3f4f6",
  textColor = "#000000",
  isEditable = false,
  onChange,
  field,
  format,
  dataType = "uint",
  // Nouvelles props pour les couleurs
  accentColor, // Couleur du trait et de l'icône
  theme, // Thème prédéfini
  showCheckbox = false, // Afficher la checkbox dans la deuxième colonne
  checkboxValue = false,
  onCheckboxChange,
  style = {}, // Nouveau prop pour les styles personnalisés
}) => {
  // Déterminer la couleur d'accent à utiliser
  const finalAccentColor = accentColor || theme?.accentColor || icon?.props?.style?.color || "#6b7280";

  return (
    <div
      className="compact-financial-card-modern param-resultat-card"
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        border: "1px solid rgba(255,255,255,0.8)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "38px",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
        backdropFilter: "blur(12px)",
        margin: "2px 0",
        width: "100%", // Prendre toute la largeur par défaut
        ...style, // Appliquer les styles personnalisés
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1), 0 3px 10px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.9)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)";
      }}
    >
      {/* Ligne d'accent colorée moderne avec effet de brillance */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          background: `linear-gradient(135deg, ${finalAccentColor}, ${finalAccentColor}aa, ${finalAccentColor}66)`,
          borderRadius: "0 2px 2px 0",
          opacity: 0.9,
          boxShadow: `2px 0 8px ${finalAccentColor}33`,
        }}
      />
      
      {/* Élément décoratif en arrière-plan */}
      <div
        style={{
          position: "absolute",
          right: "-10px",
          top: "-10px",
          width: "40px",
          height: "40px",
          background: `radial-gradient(circle, ${finalAccentColor}15, transparent 70%)`,
          borderRadius: "50%",
          opacity: 0.6,
        }}
      />
      
      {/* Contenu principal */}
      <div style={{ display: "flex", alignItems: "center", width: "50%", marginLeft: "8px", zIndex: 1 }}>
        {/* Container d'icône moderne */}
        {icon && (
          <div
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "8px",
              background: `linear-gradient(135deg, ${iconColor}, ${iconColor}dd)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "10px",
              boxShadow: `0 2px 6px ${finalAccentColor}25, 0 1px 3px rgba(0,0,0,0.08)`,
              border: `1px solid ${finalAccentColor}20`,
              transition: "all 0.2s ease",
              position: "relative",
              flexShrink: 0,
            }}
          >
            {/* Effet de brillance sur l'icône */}
            <div
              style={{
                position: "absolute",
                top: "2px",
                left: "2px",
                right: "2px",
                height: "50%",
                background: "linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)",
                borderRadius: "6px 6px 8px 8px",
                pointerEvents: "none",
              }}
            />
            {React.cloneElement(icon, {
              style: {
                fontSize: "12px",
                color: finalAccentColor,
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                zIndex: 2,
                position: "relative",
              },
            })}
          </div>
        )}
        
        {/* Titre moderne avec largeur fixe */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4
            style={{
              margin: 0,
              fontSize: "13px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              color: "#374151",
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </h4>
        </div>
      </div>

      {/* Actions et valeurs en trois colonnes avec largeurs fixes */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", zIndex: 1, width: "60%", justifyContent: "space-between" }}>
        {/* Colonne 1 - Situation d'origine */}
        <div 
          style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            width: "110px",
            padding: "6px 8px",
            borderRadius: "6px",
            marginRight: "20px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              textAlign: "right",
            }}
          >
            {format ? applyFormat(valueOrigine || 0, dataType, format) : valueOrigine || 0}
          </div>
        </div>

        {/* Conteneur pour les colonnes 2 et 3 */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
          {/* Colonne 2 - Nouvelle situation (éditable) avec bouton d'édition */}
          <div 
            style={{ 
              display: "flex", 
              flexDirection: "row", 
              alignItems: "center", 
              justifyContent: "center",
              width: "130px",
              borderRadius: "6px",
              gap: "4px",
            }}
          >
            {/* Bouton d'édition à gauche ou espace réservé */}
            {onEdit ? (
              <Button onClick={onEdit} icon {...createStyledButton("card-action", "cardEdit")}>
                <FaEdit style={{ color: "#22c55e", fontSize: "12px" }} />
              </Button>
            ) : (
              <div style={{ width: "24px", height: "24px" }} /> // Espace réservé pour maintenir l'alignement
            )}
            
            {isEditable ? (
              <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1 }}>
                <TextField
                  value={valueNouvelle}
                  onChange={onChange}
                  dataType={dataType}
                  format={format || { thousands: " ", unit: " €" }}
                  style={{
                    height: "24px",
                    width: "100%",
                    padding: "0 6px",
                    backgroundColor: `${finalAccentColor}15`,
                    border: `1px solid ${finalAccentColor}30`,
                    borderRadius: "4px",
                    textAlign: "right",
                    fontSize: "12px",
                    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                    color: "#000000",
                    letterSpacing: "-0.02em",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: `inset 0 1px 2px ${finalAccentColor}10`,
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = `${finalAccentColor}60`;
                    e.currentTarget.style.backgroundColor = `${finalAccentColor}08`;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${finalAccentColor}20, inset 0 1px 2px ${finalAccentColor}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = `${finalAccentColor}30`;
                    e.currentTarget.style.backgroundColor = `${finalAccentColor}15`;
                    e.currentTarget.style.boxShadow = `inset 0 1px 2px ${finalAccentColor}10`;
                  }}
                />
                {showCheckbox && onCheckboxChange && (
                  <div style={{ display: "flex", alignItems: "center", margin: "0", padding: "0" }}>
                    <input
                      type="checkbox"
                      checked={checkboxValue}
                      onChange={(e) => onCheckboxChange(e.target.checked)}
                      style={{
                        margin: "0",
                        padding: "0",
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  fontSize: "12px",
                  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 400,
                  letterSpacing: "-0.02em",
                  textAlign: "right",
                  flex: 1,
                  paddingLeft: "4px", // Compenser l'espace du gap
                }}
              >
                {format ? applyFormat(valueNouvelle || 0, dataType, format) : valueNouvelle || 0}
              </div>
            )}
            
            {showCheckbox && onCheckboxChange && (
              <div style={{ display: "flex", alignItems: "center", margin: "0", padding: "0" }}>
                <input
                  type="checkbox"
                  checked={checkboxValue}
                  onChange={(e) => onCheckboxChange(e.target.checked)}
                  style={{
                    margin: "0",
                    padding: "0",
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                  }}
                />
              </div>
            )}
          </div>

  
        </div>
                {/* Colonne 3 - Écart (plus à droite) */}
          <div 
            style={{ 
              display: "flex", 
              flexDirection: "column", 
              alignItems: "flex-end", 
              width: "100px",
              padding: "6px 8px",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 400,
                letterSpacing: "-0.02em",
                textAlign: "right",
                color: "#374151",
              }}
            >
              {format ? applyFormat(valueEcart || 0, dataType, format) : valueEcart || ""}
            </div>
          </div>
      </div>
    </div>
  );
};

export default CompactFinancialCard;
